import { relations } from "drizzle-orm";
import {
  bigint,
  index,
  integer,
  pgEnum,
  pgTable,
  primaryKey,
  real,
  text,
  timestamp,
  unique,
  varchar,
} from "drizzle-orm/pg-core";

const id = varchar({ length: 36 }).primaryKey();

const timestamps = {
  createdAt: timestamp({ withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp({ withTimezone: true })
    .notNull()
    .defaultNow()
    .$onUpdate(() => new Date()),
};

// #region Users and authentication

export const users = pgTable("users", {
  id,
  ...timestamps,
  telegramId: bigint({ mode: "number" }).notNull(),
  telegramUsername: varchar({ length: 256 }),
  name: varchar({ length: 128 }),
  avatarUrl: varchar({ length: 256 }),
  avatarHash: varchar({ length: 256 }),
});

export const usersRelations = relations(users, ({ many }) => ({
  workspaceMemberships: many(workspaceMemberships),
  boardMemberships: many(boardMemberships),
  taskAssignments: many(taskAssignments),
}));

export const refreshTokens = pgTable(
  "refresh_tokens",
  {
    token: varchar({ length: 128 }).notNull().primaryKey(),
    createdAt: timestamp({ withTimezone: true }).notNull().defaultNow(),
    expiresAt: timestamp({ withTimezone: true }).notNull(),
    userId: varchar({ length: 36 })
      .references(() => users.id, {
        onUpdate: "cascade",
        onDelete: "cascade",
      })
      .notNull(),
  },
  (table) => [index().on(table.userId)],
);

// #endregion

// #region Workspaces

export const workspaces = pgTable("workspaces", {
  id,
  ...timestamps,
  slug: varchar({ length: 128 }).notNull().unique(),
  name: varchar({ length: 128 }).notNull(),
});

export const workspacesRelations = relations(workspaces, ({ many }) => ({
  memberships: many(workspaceMemberships),
  boards: many(boards),
}));

export const workspaceMemberRoleEnum = pgEnum("workspace_member_role", [
  "admin",
  "member",
]);

export const workspaceMemberships = pgTable(
  "workspace_memberships",
  {
    workspaceId: varchar({ length: 36 })
      .references(() => workspaces.id, {
        onUpdate: "cascade",
        onDelete: "cascade",
      })
      .notNull(),
    userId: varchar({ length: 36 })
      .references(() => users.id, {
        onUpdate: "cascade",
        onDelete: "cascade",
      })
      .notNull(),
    ...timestamps,
    role: workspaceMemberRoleEnum().notNull().default("member"),
  },
  (table) => [
    primaryKey({ columns: [table.workspaceId, table.userId] }),
    index().on(table.userId),
  ],
);

export const workspaceMembershipsRelations = relations(
  workspaceMemberships,
  ({ one }) => ({
    workspace: one(workspaces, {
      fields: [workspaceMemberships.workspaceId],
      references: [workspaces.id],
    }),
    user: one(users, {
      fields: [workspaceMemberships.userId],
      references: [users.id],
    }),
  }),
);

// #endregion

// #region Boards and Tasks

export const boardVisibilityEnum = pgEnum("board_visibility", [
  "private",
  "workspace",
]);

export const boards = pgTable(
  "boards",
  {
    id,
    ...timestamps,
    workspaceId: varchar({ length: 36 })
      .references(() => workspaces.id, {
        onUpdate: "cascade",
        onDelete: "cascade",
      })
      .notNull(),
    name: varchar({ length: 128 }).notNull(),
    boardKey: varchar({ length: 16 }).notNull(),
    visibility: boardVisibilityEnum().notNull().default("workspace"),
    icon: varchar({ length: 128 }),
    iconColor: varchar({ length: 32 }),
  },
  (table) => [unique().on(table.workspaceId, table.boardKey)],
);

export const boardsRelations = relations(boards, ({ one, many }) => ({
  workspace: one(workspaces, {
    fields: [boards.workspaceId],
    references: [workspaces.id],
  }),
  memberships: many(boardMemberships),
  lists: many(lists),
  tasks: many(tasks),
}));

export const boardMemberRoleEnum = pgEnum("board_member_role", [
  "admin",
  "member",
]);

export const boardMemberships = pgTable(
  "board_memberships",
  {
    boardId: varchar({ length: 36 })
      .references(() => boards.id, {
        onUpdate: "cascade",
        onDelete: "cascade",
      })
      .notNull(),
    userId: varchar({ length: 36 })
      .references(() => users.id, {
        onUpdate: "cascade",
        onDelete: "cascade",
      })
      .notNull(),
    ...timestamps,
    role: boardMemberRoleEnum().notNull().default("member"),
  },
  (table) => [
    primaryKey({ columns: [table.boardId, table.userId] }),
    index().on(table.userId),
  ],
);

export const boardMembershipsRelations = relations(
  boardMemberships,
  ({ one }) => ({
    board: one(boards, {
      fields: [boardMemberships.boardId],
      references: [boards.id],
    }),
    user: one(users, {
      fields: [boardMemberships.userId],
      references: [users.id],
    }),
  }),
);

export const lists = pgTable(
  "lists",
  {
    id,
    ...timestamps,
    boardId: varchar({ length: 36 })
      .references(() => boards.id, {
        onUpdate: "cascade",
        onDelete: "cascade",
      })
      .notNull(),
    name: varchar({ length: 128 }).notNull(),
    position: real().notNull(),
  },
  (table) => [index().on(table.boardId)],
);

export const listsRelations = relations(lists, ({ one, many }) => ({
  board: one(boards, {
    fields: [lists.boardId],
    references: [boards.id],
  }),
  tasks: many(tasks),
}));

export const tasks = pgTable(
  "tasks",
  {
    id,
    ...timestamps,
    boardId: varchar({ length: 36 })
      .references(() => boards.id, {
        onUpdate: "cascade",
        onDelete: "cascade",
      })
      .notNull(),
    taskNumber: integer().notNull().default(0),
    listId: varchar({ length: 36 })
      .references(() => lists.id, {
        onUpdate: "cascade",
        onDelete: "restrict",
      })
      .notNull(),
    position: real().notNull(),
    name: varchar({ length: 256 }).notNull(),
    description: text(),
  },
  (table) => [
    unique().on(table.boardId, table.taskNumber),
    index().on(table.listId, table.position),
  ],
);

export const tasksRelations = relations(tasks, ({ one, many }) => ({
  board: one(boards, {
    fields: [tasks.boardId],
    references: [boards.id],
  }),
  list: one(lists, {
    fields: [tasks.listId],
    references: [lists.id],
  }),
  assignments: many(taskAssignments),
}));

export const taskAssignments = pgTable(
  "task_assignments",
  {
    taskId: varchar({ length: 36 })
      .references(() => tasks.id, {
        onUpdate: "cascade",
        onDelete: "cascade",
      })
      .notNull(),
    userId: varchar({ length: 36 })
      .references(() => users.id, {
        onUpdate: "cascade",
        onDelete: "cascade",
      })
      .notNull(),
  },
  (table) => [
    primaryKey({ columns: [table.taskId, table.userId] }),
    index().on(table.userId),
  ],
);

export const taskAssignmentsRelations = relations(
  taskAssignments,
  ({ one }) => ({
    task: one(tasks, {
      fields: [taskAssignments.taskId],
      references: [tasks.id],
    }),
    user: one(users, {
      fields: [taskAssignments.userId],
      references: [users.id],
    }),
  }),
);

// #endregion
